// ---- Combat (client) ----
// Melee combat layered on top of the focus system. The SERVER is authoritative
// for every fightable NPC — it runs the chase/punch AI and owns HP/stamina/defeat
// (see index.ts). This module is the client half: it sends the player's combat
// *intents* (focus → engage/disengage in focus.ts; punch → `attack`), renders the
// reactions the server signals (NPC flinch/lunge/topple, written onto each Live by
// net.ts), and drives the local HUD (overhead HP/STA bars + the bottom action bar).
//
// Focusing a fightable NPC is camera + action-bar only (F = primary attack) — it does
// NOT make the NPC attack. HP/stamina bars float when the server marks it engaged
// (a fight is on). Since plan 14, fights start TWO-WAY via territory aggro / pack
// assist / a landed hit — the server streams the NPC's player target as
// `NetNpc.tgt`, and `tgt === me.id` pops the engaged presentation with no click.
// A punch is gated locally by cooldown + stamina (so it feels instant) and predicts a
// flinch; the server applies the real damage and broadcasts the result.

import * as THREE from "three";
import { reachDist } from "./shared.ts";
import { game, inputLocked } from "./state.ts";
import { npcs, type Live } from "./npc/index.ts";
import { getFocus, clearFocus, getPropFocus } from "./focus.ts";
import { talkSlotState } from "./talk.ts";
import { interactSlotState, INTERACT_CD } from "./interact.ts";
import { doorSlotState } from "./doors.ts";
import { fishSlotState, netSlotState, REEL_CD } from "./fishing.ts";
import { trapSlotState } from "./hunting.ts";
import { cookSlotState } from "./cooking.ts";
import { extinguishSlotState } from "./campfire.ts";
import { brewSlotState } from "./brewing.ts";

import { cancelChop } from "./woodcutting.ts";
import { startAttack } from "./player.ts";
import {
  me, sendAttack, sendFire, sendFireMove, sendSwing, sendReady, sendFighting, sendBlock,
  sendChargeBegin, sendChargeCancel, sendChargeRelease, combatants,
} from "./net.ts";
import { initPlayerBars, renderPlayerBars, freshBarCss, hideBar, placeBar, fillBar, labelBar, setStatusIcons } from "./playerbars.ts";
import { initDmgFloats, updateDmgFloats } from "./dmgfloat.ts";
import { updateCombatFx } from "./combatfx.ts";
import { equippedType, equippedWeapon, equippedShieldType, countOf, flashToast } from "./inventory.ts";
import { itemSpec } from "./items/index.ts";
import { isRanged } from "./items/define.ts";
import { weaponMoveSpecs, moveRangeMult, moveCast, moveDelivery, type MoveSpec } from "./moves.ts";
import { hasBoxUnlocked } from "./skill.ts";
import { startCooldown, onCooldown, tickCooldowns, renderCooldownSweep, renderSlotButton, setSlotHotkey } from "./cooldown.ts";
import { ROAD_BOOST } from "./config.ts";
import {
  popFlinch, applyFlinch, popLunge, applyLunge, toppleK,
  FLINCH_DUR, FLINCH_POP, DEFEAT_DUR,
} from "./anim/reactions.ts";
import { setAirborne, popLanding, applyAirborne } from "./anim/airborne.ts";
// The combat-economy tunables live in src/sim/combat.ts — ONE home shared with
// the server and the studio sandbox, so a balance change can't drift.
import { STA_REGEN, RUN_STA_DRAIN, PLAYER_PUNCH_CD, PUNCH_STA_COST } from "./sim/combat.ts";
import { playSfx, preloadSfx } from "./audio.ts";
import { chatTyping } from "./chat.ts";
import { keys } from "./state.ts";

// The hit/topple reaction primitives live in src/anim/reactions.ts (shared with
// the studio's skills simulator so feel can't drift); re-exported for net.ts.
export { FLINCH_DUR, FLINCH_POP, DEFEAT_DUR };

/** Unarmed punch one-shot (assets/audio/punch-1.opus) — every player punch. */
const PUNCH_SFX = "punch-1";
/** Wolf snarl/bark one-shots — random pick, only on some attacks (see playNpcAttackSfx). */
const WOLF_ATTACK_SFX = ["wolf-attack1", "wolf-attack2", "wolf-attack3"] as const;
/** Chance a wolf attack plays an SFX (~every 2nd–3rd strike on average). */
const WOLF_SFX_CHANCE = 0.4;

/** Seconds the corpse stays on the ground after defeat (includes the topple),
 *  then CORPSE_FADE sinks/scales it out. Presentation only — the server still
 *  owns the full combat.respawn / RESPAWN_DELAY wait. */
export const CORPSE_LINGER = 5;
/** Seconds of sink+scale-out after CORPSE_LINGER before the mesh hides. */
export const CORPSE_FADE = 0.9;

const _v = new THREE.Vector3();

let camera: THREE.Camera;
let actionbar: HTMLElement | null = null;
let punchBtn: HTMLButtonElement | null = null;
let moveBtns: { btn: HTMLButtonElement; cd: HTMLElement }[] = []; // ability slots 1–4 (empty placeholders fill the fixed combat strip)
let talkBtn: HTMLButtonElement | null = null;
let talkLabelShown = ""; // last label rendered into the Talk slot (avoid DOM churn)
let interactBtn: HTMLButtonElement | null = null;
let interactShown = ""; // last icon+label rendered into the Interact slot
let fishBtn: HTMLButtonElement | null = null;  // Cast/Reel slot (plan 10; fishing.ts binds the click)
let fishCd: HTMLElement | null = null;         // its reel-window sweep (the visible reaction timer)
let netBtn: HTMLButtonElement | null = null;   // Set Net / Collect slot
let trapBtn: HTMLButtonElement | null = null;  // Set Trap / Collect slot (plan 11; hunting.ts binds the click)
let trapCd: HTMLElement | null = null;
let netCd: HTMLElement | null = null;
let cookBtn: HTMLButtonElement | null = null;  // Cooking / Pull slot (plan 18; cooking.ts binds the click)
let cookCd: HTMLElement | null = null;         // its cook-fill + pull-window sweep (cook.pull)
let brewBtn: HTMLButtonElement | null = null;  // Ferment / Collect slot (plan 18 CP3; brewing.ts binds the click)
let extinguishBtn: HTMLButtonElement | null = null; // put out own focused campfire
let extinguishCd: HTMLElement | null = null; // placeholder overlay so renderSlotButton can key labels
let brewCd: HTMLElement | null = null;
let cdOverlay: HTMLElement | null = null; // radial cooldown-sweep overlay on the attack slot
let interactCd: HTMLElement | null = null; // cooldown sweep on the interact slot

// The player's attack action shares one cooldown key across punch + every weapon
// (melee swing or bow shot) — one slot, one recharge. Routed through the reusable
// cooldown registry (src/cooldown.ts) so the action bar shows a visible sweep and
// future actions can arm their own keyed cooldowns the same way.
const ATTACK_CD = "player.attack";
let lastReady = false; // last combat-ready state sent to the server (edge-detect)
let lastFighting = false; // last in-combat state sent to the server (edge-detect)
let downFallT = 0; // topple-animation progress (seconds into the fall); the server owns the revive

// ---- Power-shot charge (hold ability key) -----------------------------------
type ChargeState = {
  move: MoveSpec;
  slot: number; // 0..3 ability slot
  startedAt: number;
  chargeTime: number;
};
let charge: ChargeState | null = null;

function syncChargeGameFlags(): void {
  if (!charge) {
    game.bowCharging = false;
    game.bowChargeReady = false;
    game.bowChargeFrac = 0;
    return;
  }
  const frac = Math.min(1, (performance.now() - charge.startedAt) / 1000 / charge.chargeTime);
  game.bowCharging = true;
  game.bowChargeFrac = frac;
  game.bowChargeReady = frac >= 1;
}

/** Free cancel — no ammo/sta/CD. Always clears charge UI (slot + focus dial). */
export function cancelBowCharge(): void {
  if (!charge) return;
  charge = null;
  sendChargeCancel();
  syncChargeGameFlags();
  clearChargeVisuals();
}

/** Drop any leftover charge swirl styles (inline conic must not stick after cancel). */
function clearChargeVisuals(): void {
  for (const { btn, cd } of moveBtns) {
    btn.classList.remove("charging", "charge-ready");
    // Restore default cooldown overlay (was overridden while charging).
    cd.style.background = "";
    cd.style.opacity = "";
  }
  if (focusChargeEl) {
    focusChargeEl.style.display = "none";
    focusChargeEl.classList.remove("ready");
    focusChargeEl.style.background = "";
  }
}

function beginBowCharge(mv: MoveSpec, slot: number): void {
  if (charge) return; // already holding
  if (inputLocked() || game.stunT > 0) return;
  const w = equippedWeapon();
  if (!w || !isRanged(w) || moveCast(mv) !== "charge") return;
  if (mv.requiresBox && !hasBoxUnlocked(mv.requiresBox)) return;
  const key = `player.move.${mv.id}`;
  if (onCooldown(key) || game.stamina < mv.staminaCost) return;
  if (!target()) { flashToast("No target"); return; }
  if (countOf(w.ranged.ammo) < 1) {
    flashToast(`Out of ${itemSpec(w.ranged.ammo).name.toLowerCase()}s`);
    return;
  }
  charge = {
    move: mv,
    slot,
    startedAt: performance.now(),
    chargeTime: mv.chargeTime ?? 1.2,
  };
  sendChargeBegin(mv.id);
  syncChargeGameFlags();
}

/** Focus-card power-shot dial — soft gold conic under the target portrait. */
let focusChargeEl: HTMLElement | null = null;
/** Charge swirl fill opacity (subtle — not a solid gold plate). */
const CHARGE_SWIRL_A = 0.2;
function paintFocusChargeDial(): void {
  const root = document.getElementById("focus");
  if (!root) return;
  if (!focusChargeEl) {
    focusChargeEl = document.createElement("div");
    focusChargeEl.id = "focusCharge";
    focusChargeEl.className = "focus-charge";
    root.appendChild(focusChargeEl);
  }
  if (!charge || game.bowChargeFrac <= 0) {
    focusChargeEl.style.display = "none";
    focusChargeEl.classList.remove("ready");
    focusChargeEl.style.background = "";
    return;
  }
  const pct = Math.round(game.bowChargeFrac * 100);
  focusChargeEl.style.display = "block";
  // Soft gold fill only — transparent remainder so the card still reads.
  focusChargeEl.style.background =
    `conic-gradient(rgba(232,197,71,${CHARGE_SWIRL_A}) ${pct}%, transparent ${pct}%)`;
  focusChargeEl.classList.toggle("ready", game.bowChargeReady);
}

function releaseBowCharge(): void {
  if (!charge) return;
  const mv = charge.move;
  const frac = Math.min(1, (performance.now() - charge.startedAt) / 1000 / charge.chargeTime);
  charge = null;
  syncChargeGameFlags();
  clearChargeVisuals();
  if (frac < 1) {
    // Incomplete — free cancel, no shot.
    sendChargeCancel();
    return;
  }
  const w = equippedWeapon();
  const L = target();
  if (!w || !isRanged(w) || !L) {
    sendChargeCancel();
    flashToast(!L ? "No target" : "Need a bow equipped");
    return;
  }
  // Optimistic spend/CD (server re-validates full charge + spends).
  game.stamina = Math.max(0, game.stamina - mv.staminaCost);
  startCooldown(`player.move.${mv.id}`, mv.cooldown);
  const sel = specialSwingSel(mv);
  startAttack(game.player, sel);
  sendSwing(sel);
  sendChargeRelease(L.spec.id);
}

export function initCombat(cam: THREE.Camera): void {
  camera = cam;
  // Hold-to-block (plan 14, proposal: hold right-mouse — E is orbit, F is
  // interact). Window-level so a drag off the canvas still releases; the game
  // canvas keeps its context menu suppressed so RMB is free to be an input.
  document.getElementById("game")?.addEventListener("contextmenu", (e) => e.preventDefault());
  window.addEventListener("pointerdown", (e) => { if (e.button === 2) setBlocking(true); });
  window.addEventListener("pointerup", (e) => { if (e.button === 2) setBlocking(false); });
  window.addEventListener("blur", () => {
    setBlocking(false);
    if (charge) cancelBowCharge(); // lose focus = free cancel, never accidental fire
  });
  actionbar = document.getElementById("actionbar");
  punchBtn = document.getElementById("punchBtn") as HTMLButtonElement | null;
  talkBtn = document.getElementById("talkBtn") as HTMLButtonElement | null;
  interactBtn = document.getElementById("interactBtn") as HTMLButtonElement | null;
  fishBtn = document.getElementById("fishBtn") as HTMLButtonElement | null;
  netBtn = document.getElementById("netBtn") as HTMLButtonElement | null;
  trapBtn = document.getElementById("trapBtn") as HTMLButtonElement | null;
  cookBtn = document.getElementById("cookBtn") as HTMLButtonElement | null;
  brewBtn = document.getElementById("brewBtn") as HTMLButtonElement | null;
  extinguishBtn = document.getElementById("extinguishBtn") as HTMLButtonElement | null;
  if (extinguishBtn) {
    // Stable child for renderSlotButton (never wipe the button every frame —
    // per-frame innerHTML was swallowing clicks until a lucky frame).
    extinguishCd = document.createElement("span");
    extinguishCd.className = "cooldown";
    extinguishBtn.appendChild(extinguishCd);
  }
  if (fishBtn) {
    fishCd = document.createElement("span");
    fishCd.className = "cooldown";
    fishBtn.appendChild(fishCd);
  }
  if (netBtn) {
    netCd = document.createElement("span");
    netCd.className = "cooldown";
    netBtn.appendChild(netCd);
  }
  if (trapBtn) {
    trapCd = document.createElement("span");
    trapCd.className = "cooldown";
    trapBtn.appendChild(trapCd);
  }
  if (brewBtn) {
    brewCd = document.createElement("span");
    brewCd.className = "cooldown";
    brewBtn.appendChild(brewCd);
  }
  if (cookBtn) {
    cookCd = document.createElement("span");
    cookCd.className = "cooldown";
    cookBtn.appendChild(cookCd);
  }
  punchBtn?.addEventListener("click", playerPunch);
  // Combat-strip hotkeys: F = primary attack; 1–4 = abilities. Only while a
  // combat target is live (matches the slots' visibility). Cooldown + stamina
  // gate the swing so key-repeat is free. Chat swallows keys while typing.
  // Prop Interact keeps F when a prop is focused (interact.ts registers first).
  // Charge moves: keydown begins, keyup releases (full = fire, partial = cancel).
  addEventListener("keydown", (e) => {
    if (chatTyping()) return;
    if (e.repeat) return;
    if (!target()) return; // not in combat — leave F/digits free for interact/dialogue
    if (e.key.toLowerCase() === "f") {
      // Prop focus owns F (Open / Shake / Pick / Read); doors yield via game.fighting.
      if (getPropFocus()) return;
      if (charge) cancelBowCharge(); // F cancels a charge rather than dual-firing
      playerPunch();
      return;
    }
    const mi = e.key === "1" ? 0 : e.key === "2" ? 1 : e.key === "3" ? 2 : e.key === "4" ? 3 : -1;
    if (mi < 0) return;
    const mv = weaponMoves()[mi];
    if (!mv) return;
    if (moveCast(mv) === "charge") beginBowCharge(mv, mi);
    else playerMove(mv);
  });
  addEventListener("keyup", (e) => {
    if (!charge) return;
    const mi = e.key === "1" ? 0 : e.key === "2" ? 1 : e.key === "3" ? 2 : e.key === "4" ? 3 : -1;
    if (mi === charge.slot) releaseBowCharge();
  });
  // The attack slot's cooldown-sweep overlay (a conic recharge wipe). Persistent —
  // updateCombat re-appends it after any label rebuild so it survives innerHTML churn.
  if (punchBtn) {
    cdOverlay = document.createElement("span");
    cdOverlay.className = "cooldown";
    punchBtn.appendChild(cdOverlay);
  }
  // Ability slots 1–4 (plan 14) — populated from the equipped weapon's `moves`
  // list each frame; empty slots stay visible as placeholders so the combat
  // strip is always F + four ability slots. Each filled slot has its own cooldown.
  moveBtns = [];
  for (const bid of ["move1Btn", "move2Btn", "move3Btn", "move4Btn"]) {
    const btn = document.getElementById(bid) as HTMLButtonElement | null;
    if (!btn) continue;
    const cd = document.createElement("span");
    cd.className = "cooldown";
    btn.appendChild(cd);
    const slot = moveBtns.length;
    // Charge moves need press/hold on the button; instant stays click.
    // setPointerCapture so drag-off still gets pointerup/cancel (not sticky root).
    btn.addEventListener("pointerdown", (e) => {
      if (e.button !== 0) return;
      const mv = weaponMoves()[slot];
      if (!mv) return;
      if (moveCast(mv) === "charge") {
        e.preventDefault();
        try { btn.setPointerCapture(e.pointerId); } catch { /* already captured */ }
        beginBowCharge(mv, slot);
      }
    });
    const endChargePointer = (e: PointerEvent) => {
      if (e.button !== 0 && e.type !== "pointercancel") return;
      if (charge && charge.slot === slot) {
        if (e.type === "pointercancel") cancelBowCharge();
        else releaseBowCharge();
      }
      try { if (btn.hasPointerCapture(e.pointerId)) btn.releasePointerCapture(e.pointerId); } catch { /* ok */ }
    };
    btn.addEventListener("pointerup", endChargePointer);
    btn.addEventListener("pointercancel", endChargePointer);
    btn.addEventListener("click", () => {
      const mv = weaponMoves()[slot];
      if (mv && moveCast(mv) !== "charge") playerMove(mv);
    });
    moveBtns.push({ btn, cd });
  }
  initPlayerBars();
  initDmgFloats();
  // Combat FX scene is bound from game.ts (needs the live scene); optional here.
  // Warm combat one-shots so the first punch / wolf snarl isn't silent.
  preloadSfx(PUNCH_SFX, ...WOLF_ATTACK_SFX);
}

/** The player's combat target: the focused, fightable, living NPC — or, with
 *  nothing focused (plan 14: engagement no longer requires focus), the nearest
 *  NPC currently attacking us (`tgt === me.id` — the server streams each NPC's
 *  player target). Focus stays the aiming/camera aid; auto-engage just means
 *  fighting back doesn't need the click. Single pass, no allocation — this
 *  runs every frame from updateCombat. */
function target(): Live | null {
  const f = getFocus() as Live | null;
  if (f && f.fightable && !f.defeated) return f;
  let best: Live | null = null, bd = Infinity;
  for (const L of npcs()) {
    if (L.tgt !== me.id || L.defeated || !L.fightable) continue;
    const d = dist(L);
    if (d < bd) { bd = d; best = L; }
  }
  return best;
}

function dist(L: Live): number {
  const p = game.player.position, q = L.group.position;
  return reachDist(p.x, game.vert.y, p.z, q.x, L.feetY, q.z); // Infinity a floor apart (gravity plan)
}

/** Player attacks (wired to the action-bar button). Unarmed it's a punch; with a
 *  melee weapon its damage/reach/cooldown/stamina + swing style come from the spec;
 *  with a RANGED weapon it fires a projectile instead (see playerFire). The melee
 *  swing always plays when stamina + cooldown allow; the server decides whether it
 *  connects (and applies the damage) — we just predict a flinch for snappy feel. */
export function playerPunch(): void {
  if (inputLocked() || game.stunT > 0) return; // can't swing while locked or stunned
  const w = equippedWeapon();
  if (w && isRanged(w)) { playerFire(w); return; } // bow: fire an arrow, not a swing
  const cost = w ? w.staminaCost : PUNCH_STA_COST;
  if (onCooldown(ATTACK_CD) || game.stamina < cost) return;

  // The swing always plays (and costs stamina + starts the cooldown).
  game.stamina -= cost;
  startCooldown(ATTACK_CD, w ? w.attackCooldown : PLAYER_PUNCH_CD);
  // "primary" = this rig's equipped-weapon (or unarmed) attack; remotes resolve
  // it against their own copy of our equipped weapon, so the wire stays agnostic.
  startAttack(game.player, "primary");
  sendSwing("primary"); // replay the same swing on our rig for every other client
  // Unarmed punch SFX every swing; melee/ranged keep silent until they have clips.
  if (!w) playSfx(PUNCH_SFX);

  // Only bother the server if a living fightable is in reach — it resolves the
  // swing as a frontal-arc CLEAVE (plan 14), so anything nearby can be struck,
  // not just the focused target. The focused NPC rides as the primary hint (a
  // deliberate attack on a non-hostile).
  const range = w ? w.attackRange : 2.2;
  if (!anyFightableInReach(range)) return;
  const L = target();
  sendAttack(L?.spec.id);
  if (L && dist(L) <= range) popFlinch(L.group); // optimistic reaction; real HP comes back in the snapshot
}

/** NPC attack presentation SFX (client-only, fired off the atkSeq edge in net.ts).
 *  Wolves (and scrawny variants): pick one of three snarls at random, and only
 *  sometimes fire so packs don't bark on every bite. Other kinds: silent for now. */
export function playNpcAttackSfx(L: Live): void {
  if (!isWolfKind(L)) return;
  if (Math.random() >= WOLF_SFX_CHANCE) return;
  playSfx(WOLF_ATTACK_SFX[(Math.random() * WOLF_ATTACK_SFX.length) | 0]!);
}

function isWolfKind(L: Live): boolean {
  // model is inherited by scrawny-wolf (base: wolf); kind covers explicit docs.
  if (L.spec.model === "wolf") return true;
  const k = L.spec.kind ?? "";
  return k === "wolf" || k.endsWith("-wolf");
}

// The special moves the equipped weapon supports (ability slots 1–4), resolved
// through the shared slot-fill rule (moves.ts weaponMoveSpecs — the studio
// arena uses the same one). Cached per equipped type: updateCombat asks every
// frame but the answer only changes on equip.
let movesFor: string | null | undefined; // equipped type the cache was built for
let movesCache: MoveSpec[] = [];
function weaponMoves(): MoveSpec[] {
  const t = equippedType();
  if (t !== movesFor) {
    movesFor = t;
    const w = equippedWeapon();
    // Melee + ranged both fill ability slots (bow specials are delivery:ranged).
    movesCache = w ? weaponMoveSpecs(w) : [];
  }
  return movesCache;
}

/** Any living fightable NPC within `range` of us? — the "worth telling the
 *  server" check now that a swing cleaves (the focused target is no longer the
 *  only thing a swing can hit). */
function anyFightableInReach(range: number): boolean {
  for (const L of npcs()) {
    if (!L.fightable || L.defeated || !L.group.visible) continue;
    if (dist(L) <= range) return true;
  }
  return false;
}

/** Use a special ability (action-bar slots 1–4, plan 14). Melee = cleave attack;
 *  ranged instant = fireMove; charge moves use beginBowCharge/releaseBowCharge. */
function playerMove(mv: MoveSpec): void {
  if (inputLocked() || game.stunT > 0) return;
  if (charge) cancelBowCharge();
  const w = equippedWeapon();
  if (!w || !w.moves?.includes(mv.id)) return;
  if (mv.requiresBox && !hasBoxUnlocked(mv.requiresBox)) return; // locked (slot shows 🔒)
  if (moveCast(mv) === "charge") return; // hold path only
  const key = `player.move.${mv.id}`;
  if (onCooldown(key) || game.stamina < mv.staminaCost) return;

  // ---- Ranged instant special (slow-arrow, …) ----
  if (isRanged(w) && moveDelivery(mv) === "ranged") {
    const L = target();
    if (!L) { flashToast("No target"); return; }
    if (dist(L) > w.attackRange) { flashToast("Out of range"); return; }
    if (countOf(w.ranged.ammo) < 1) {
      flashToast(`Out of ${itemSpec(w.ranged.ammo).name.toLowerCase()}s`);
      return;
    }
    game.stamina -= mv.staminaCost;
    startCooldown(key, mv.cooldown);
    const sel = specialSwingSel(mv);
    startAttack(game.player, sel);
    sendSwing(sel);
    sendFireMove(L.spec.id, mv.id);
    return;
  }

  // ---- Melee special ----
  if (isRanged(w)) return;
  game.stamina -= mv.staminaCost;
  startCooldown(key, mv.cooldown);
  startAttack(game.player, mv.clip);
  sendSwing(mv.clip);

  // Always sendAttack for specials — server spends on whiff and broadcasts the
  // `{t:"aoe"}` volume flash even when nobody is in the shape (telegraph is truth).
  const range = w.attackRange * moveRangeMult(mv);
  const L = target();
  sendAttack(L?.spec.id, mv.id);
  if (L && dist(L) <= range) popFlinch(L.group);
}

/** Fire a ranged weapon (focus-locked). Every gate is checked BEFORE committing so
 *  a blocked shot spends no cooldown/stamina/ammo:
 *   • must have a focused, living, fightable target,
 *   • the target must be within the hard fire cap (last band's max = attackRange),
 *   • must hold ≥1 of the required ammo.
 *  On a valid shot we spend one arrow + stamina + cooldown, play the draw, and send
 *  the `fire` intent. The server rolls the distance-band hit chance, broadcasts the
 *  arrow to everyone (incl. us), and lands the damage/flinch on arrival — so we
 *  DON'T predict a flinch or spawn the projectile locally (both come from the wire). */
function playerFire(w: NonNullable<ReturnType<typeof equippedWeapon>>): void {
  if (!isRanged(w)) return;
  const cost = w.staminaCost;
  if (onCooldown(ATTACK_CD) || game.stamina < cost) return; // still on cooldown / winded

  const L = target();
  if (!L) { flashToast("No target"); return; }
  if (dist(L) > w.attackRange) { flashToast("Out of range"); return; }
  if (countOf(w.ranged.ammo) < 1) {
    flashToast(`Out of ${itemSpec(w.ranged.ammo).name.toLowerCase()}s`);
    return;
  }

  // Commit: spend stamina + cooldown, play the draw, ask the server. The SERVER
  // spends the arrow (Step 2 — inventory is server-owned) and echoes the new bag, so
  // the ammo badge ticks down on the `inventory` message, not a local decrement.
  game.stamina -= cost;
  startCooldown(ATTACK_CD, w.attackCooldown);
  startAttack(game.player, "primary"); // the bow's own draw clip
  sendSwing("primary"); // remotes replay the draw on our rig
  sendFire(L.spec.id);
}

// ---- Reaction triggers (called by net.ts off the server snapshot) ----------

// ---- Hold-to-block (plan 14) ------------------------------------------------
// Local intent + presentation: `blockHeld` mirrors the physical RMB; the server
// owns the truth (needs a shield, drops it on stagger/KO — see the `block`
// handler + damagePlayer in index.ts). userData.blocking drives the shared
// block pose (player.ts applyBlockPose) — remotes get theirs off the snapshot.
let blockHeld = false;

function setBlocking(on: boolean): void {
  if (on === blockHeld) return;
  // Mirror server canAct: stun rejects block raise (snare still allows it).
  if (on && (!equippedShieldType() || inputLocked() || game.stunT > 0)) return;
  blockHeld = on;
  game.player.userData.blocking = on;
  sendBlock(on);
}

/** Authoritative HP + knockout from the server (Step 3 — player HP is server-owned).
 *  net.ts calls this on every `vitals` message; we only PRESENT the server's numbers —
 *  a flinch pop on a damage tick, the topple on the knockout edge, standing up on the
 *  revive edge (the server already refilled our HP). We never decrement HP ourselves.
 *  `stag` (plan 14): a blocked hit emptied the stamina pool — the shield was knocked
 *  open; drop the local block and say so.
 *  `stamina`/`maxStamina` (skill-box bonuses, food): adopt a raised max and top up
 *  current the same way HP does. Leave client-predicted current alone when the max
 *  is unchanged so damage/CC vitals don't snap the bar mid-sprint. */
export function applyVitals(
  hp: number,
  maxHp: number,
  down: boolean,
  stag?: number,
  snare?: number,
  stun?: number,
  slow?: number,
  slowMul?: number,
  stamina?: number,
  maxStamina?: number,
): void {
  if (hp < game.hp && !game.down) popFlinch(game.player); // took damage → flinch pop
  game.hp = hp;
  game.maxHp = maxHp;
  if (typeof maxStamina === "number" && Number.isFinite(maxStamina) && maxStamina > 0) {
    const prevMax = game.maxStamina;
    const nextMax = maxStamina;
    game.maxStamina = nextMax;
    if (nextMax > prevMax) {
      // Raised cap (unlock Wind / join with bonuses): top up current by the
      // positive delta, or adopt the server value if higher (applyBoxStats
      // already topped the server pool).
      const delta = nextMax - prevMax;
      const topped = game.stamina + delta;
      const serverSta = typeof stamina === "number" && Number.isFinite(stamina) ? stamina : topped;
      game.stamina = Math.min(nextMax, Math.max(topped, serverSta));
    } else {
      // Cap flat or dropped — clamp local current; don't overwrite with server
      // current (client still predicts spends/regen between pushes).
      game.stamina = Math.min(nextMax, game.stamina);
    }
  }
  if (stag && stag > 0 && blockHeld) {
    blockHeld = false;
    game.player.userData.blocking = false;
    flashToast("Staggered!");
  }
  // Mirror CC timers (owner-only remaining seconds). Absent = 0.
  game.snareT = snare && snare > 0 ? snare : 0;
  game.stunT = stun && stun > 0 ? stun : 0;
  if (slow && slow > 0) {
    game.slowT = slow;
    game.slowMult = slowMul && slowMul > 0 && slowMul < 1 ? slowMul : game.slowMult || 0.7;
  } else {
    game.slowT = 0;
    game.slowMult = 1;
  }
  if (game.stunT > 0 && blockHeld) {
    blockHeld = false;
    game.player.userData.blocking = false;
  }
  if (down && !game.down) playerDown();     // knockout edge → bail out of the fight + topple
  else if (!down && game.down) playerUp();  // revive edge → stand back up
}

/** Swing selector for a special: prefer the move's clip; fall back to primary
 *  (bow archetype draw) when clip is missing or not resolvable. */
function specialSwingSel(mv: MoveSpec): string {
  return mv.clip || "primary";
}

/** An NPC took damage — pop its hit reaction. */
export function npcFlinch(L: Live): void {
  popFlinch(L.group);
}

/** An NPC threw a punch — start its haymaker lunge. */
export function npcLunge(L: Live): void {
  popLunge(L.group);
}

/** An NPC dropped to 0 HP: start the topple, hide its bars, make it non-solid, and
 *  release focus if we were locked onto it. (net.ts owns the `defeated` flag; this
 *  is just the local presentation.) */
export function npcDefeated(L: Live): void {
  L.defeatT = 0;
  L.group.visible = true;
  L.group.scale.setScalar(1);
  if (L.cbarWrap) L.cbarWrap.style.display = "none";
  // Shove the local collider off-world so the player walks through the corpse.
  if (L.collider) { L.collider.x = 1e6; L.collider.z = 1e6; L.collider.r = 0; }
  if (getFocus() === L) clearFocus();
}

/** An NPC respawned: stand it back up and restore its collider (net.ts repositions
 *  it each frame from the fresh snapshots). */
export function npcRespawn(L: Live): void {
  L.group.rotation.z = 0;
  L.group.scale.setScalar(1);
  L.group.visible = true;
  L.defeatT = 0;
  if (L.collider) L.collider.r = L.spec.colliderR ?? 0.6;
}

/** Player knocked out (server-signalled): bail out of the fight — the focus clear emits a
 *  disengage — and start the topple animation. The server owns the down state + the revive
 *  timer; we just play the collapse until it clears `down` via a later `vitals` message. */
function playerDown(): void {
  clearFocus();
  setBlocking(false); // the shield drops with you
  game.down = true;
  downFallT = 0;
  // Cancel local chop UI immediately (server also endChop from knockOut).
  cancelChop();
}

/** Player revived (the server cleared `down`): stand back up. HP was already refilled by the
 *  server's `vitals` message; we just reset the pose + top up local stamina. */
function playerUp(): void {
  game.down = false;
  game.player.rotation.z = 0;
  game.stamina = game.maxStamina;
}

/** Animate the knockout topple: tip onto the side over DEFEAT_DUR, then hold there until
 *  the server revives us. Runs every frame from updateCombat (after updatePlayer, so the
 *  pose override wins). No revive here — the server owns that (see playerUp). */
function advancePlayerDown(dt: number): void {
  downFallT = Math.min(DEFEAT_DUR, downFallT + dt);
  const k = downFallT / DEFEAT_DUR; // 0 → 1 fall, then holds flat
  game.player.rotation.z = k * (Math.PI / 2);
  game.player.position.y = game.feetY + k * 0.3; // rest on the grass, not half-buried
}

export function updateCombat(dt: number): void {
  // 0. Knockout: while down, the only thing that runs is the topple/revive timer.
  //    Hide the action bar and the ready stance so we don't pose mid-collapse.
  if (game.down) {
    if (charge) cancelBowCharge();
    else clearChargeVisuals();
    advancePlayerDown(dt);
    if (actionbar) actionbar.style.display = "none";
    game.player.userData.combatReady = false;
    game.fighting = false;
    if (lastReady) { lastReady = false; sendReady(false); }
    if (lastFighting) { lastFighting = false; sendFighting(false); }
    renderPlayerBars(camera, combatants()); // keep remotes' bars current while we're down
    updateDmgFloats(camera, dt); // finish mid-flight numbers while down
    return;
  }

  // 0b. Power-shot charge: free cancel on WASD / jump / lost target / disengage / stun.
  if (charge) {
    const walk =
      keys.has("w") || keys.has("a") || keys.has("s") || keys.has("d") ||
      keys.has("arrowup") || keys.has("arrowdown") || keys.has("arrowleft") || keys.has("arrowright");
    // target() null = Esc focus release, death, leave range of auto-engage, etc.
    if (walk || keys.has(" ") || game.stunT > 0 || !target() || inputLocked()) {
      cancelBowCharge();
    } else {
      syncChargeGameFlags();
    }
  }

  // 1. Player regen + cooldown tick (the local player is still client-owned).
  tickCooldowns(dt);
  // Running drains stamina; otherwise it regenerates — 1.25× faster on a road.
  // Woodcutting freezes regen while the cut is live (server matches via p.chopping).
  if (game.running) {
    game.stamina = Math.max(0, game.stamina - RUN_STA_DRAIN * dt);
  } else if (!game.chopping && game.stamina < game.maxStamina) {
    const regen = STA_REGEN * (game.roadBoost ? ROAD_BOOST : 1);
    game.stamina = Math.min(game.maxStamina, game.stamina + regen * dt);
  }

  // 2. NPC reactions: ease flinch pops, punch lunges, and the defeat topple. The
  //    NPC's *state* (position, hp, engaged, defeated) is server-owned and written
  //    onto each Live by net.ts; here we only animate what it signals. Runs after
  //    updateNpcs/updateNetNpcs so these overrides win over the networked transform.
  //    Defeated: only advanceDefeat (it owns scale/visibility — flinch must not
  //    overwrite the corpse sink).
  for (const L of npcs()) {
    if (L.defeated) { advanceDefeat(L, dt); continue; }
    // Airborne lean + landing dip (gravity plan): the wire carries only the
    // flag (no vy), so a fixed falling tip reads the flight; the dip fires on
    // the flag's falling edge.
    const wasAir = L.group.userData.wasAir === true;
    if (wasAir && !L.air) popLanding(L.group);
    L.group.userData.wasAir = L.air;
    setAirborne(L.group, L.air, L.air ? -8 : 0);
    applyAirborne(L.group, dt);
    applyFlinch(L.group, dt);
    applyLunge(L.group, dt);
  }
  applyFlinch(game.player, dt);

  // 2b. Floating damage numbers + combat CC VFX.
  updateDmgFloats(camera, dt);
  updateCombatFx(dt);
  // Local CC timer prediction (server re-syncs via vitals).
  if (game.snareT > 0) game.snareT = Math.max(0, game.snareT - dt);
  if (game.stunT > 0) game.stunT = Math.max(0, game.stunT - dt);
  if (game.slowT > 0) {
    game.slowT = Math.max(0, game.slowT - dt);
    if (game.slowT <= 0) game.slowMult = 1;
  }
  if (game.kbT > 0) {
    game.kbT = Math.max(0, game.kbT - dt);
    if (game.kbT <= 0) game.kbLocal = false;
  }

  // 3. Overhead HP/STA bars over EVERY engaged NPC (so observers see other players'
  //    fights too, not just our own). The projected-bar write path (position +
  //    fills, all diff-before-write) is the SHARED playerbars.ts helpers, so an
  //    idle stand-off costs zero DOM churn and the write rules can't drift from
  //    the remote-player bars. The cache rides the Live record (cbarCss).
  const W = window.innerWidth, H = window.innerHeight;
  for (const L of npcs()) {
    if (!L.cbarWrap) continue;
    const c = (L.cbarCss ??= freshBarCss());
    if (!L.engaged || L.defeated) { hideBar(L.cbarWrap, c); continue; }
    const p = L.group.position;
    _v.set(p.x, L.feetY + L.height + 0.6, p.z).project(camera);
    if (!placeBar(L.cbarWrap, c, _v, W, H)) continue; // behind the camera
    if (L.hpFill) fillBar(L.hpFill, c, "f1", L.hp / L.maxHp);
    if (L.staFill) fillBar(L.staFill, c, "f2", L.stamina / L.maxStamina);
    labelBar(L.hpLabel, c, "t1", L.hp, L.maxHp);
    labelBar(L.staLabel, c, "t2", L.stamina, L.maxStamina);
    setStatusIcons(L.statusIcons, L.snare, L.stun, L.slow);
  }

  // 4. Action bar: shown while WE are engaged — focused on something
  //    *engageable* (a fightable or talkable NPC, or an interactable prop) OR
  //    under attack (plan 14 auto-engage: a territorial/assisting NPC started
  //    it, no click needed — target() falls back to the nearest attacker).
  //    Slot 1 (Punch/weapon) is the combat action: shown whenever we have a
  //    combat target, focused or not. Slot 2 (Talk) is driven by talk.ts; the
  //    Interact slot by interact.ts (the primary — and only — slot while a
  //    prop is focused). Within a combat engagement you can attack any time —
  //    only cooldown + stamina gate the swing.
  const T = target();
  const engaged = !!T; // the combat slots follow the target, focused or auto
  const talk = talkSlotState();
  const inter = interactSlotState();
  const door = doorSlotState();
  // Shared Interact slot: prop focus wins; else near-door Open/Close.
  const slot = inter.show ? inter : door;
  const fish = fishSlotState();
  const net = netSlotState();
  const trap = trapSlotState();
  const cook = cookSlotState();
  const brew = brewSlotState();
  const extinguish = extinguishSlotState();
  const engageable = (engaged || talk.show || slot.show || fish.show || net.show || trap.show || cook.show || brew.show || extinguish.show);
  const eqType = equippedType();
  const w = equippedWeapon();
  const ready = engaged && eqType !== null;
  game.player.userData.combatReady = ready;
  if (ready !== lastReady) { lastReady = ready; sendReady(ready); } // tell remotes
  // In-combat flag drives the overhead HP bar remotes show over us.
  if (engaged !== lastFighting) { lastFighting = engaged; sendFighting(engaged); }
  if (actionbar) actionbar.style.display = engageable ? "flex" : "none";
  // Doors yield F while a fight is live (primary attack owns it).
  game.fighting = engaged;
  if (punchBtn && cdOverlay) {
    // Primary attack (F): only meaningful for a fightable target. For a talk-only
    // NPC it's hidden so Talk reads as the primary option (flexbox collapses the gap).
    punchBtn.style.display = engaged ? "flex" : "none";
    // A ranged weapon shows its live ammo count in the slot; the ammo count rides
    // the label key so the slot rebuilds when it ticks down, not only on re-equip.
    const ranged = w && isRanged(w) ? w : null;
    const ammoLeft = ranged ? countOf(ranged.ranged.ammo) : -1;
    // Cooldown + stamina gate; a ranged weapon also greys out with no ammo.
    const cost = w ? w.staminaCost : PUNCH_STA_COST;
    renderSlotButton(
      punchBtn, cdOverlay,
      `${eqType ?? ""}|${ammoLeft}`,
      () => {
        const icon = eqType ? itemSpec(eqType).icon : "👊";
        const name = eqType ? itemSpec(eqType).name : "Punch";
        const ammoTag = ranged ? ` <span class="ammo">×${ammoLeft}</span>` : "";
        return `<span class="icon">${icon}</span>${name}${ammoTag}`;
      },
      onCooldown(ATTACK_CD) || game.stamina < cost || (ranged !== null && ammoLeft < 1),
      ATTACK_CD,
      "F", // primary attack hotkey (keydown above)
    );
  }
  // Ability slots 1–4: the equipped weapon's special moves (plan 14). Always
  // visible while engaged so the combat strip stays F + four ability boxes;
  // empty slots render as disabled placeholders but still show their hotkey.
  // Filled slots use the shared swept-slot pattern with their OWN cooldown key;
  // a locked box shows 🔒 (server re-validates).
  // Disengage with a live charge (e.g. focus cleared mid-hold) — kill charge + UI.
  if (!engaged && charge) cancelBowCharge();

  const mvs = weaponMoves();
  moveBtns.forEach(({ btn, cd }, i) => {
    if (!engaged) {
      btn.style.display = "none";
      btn.classList.remove("empty", "charging", "charge-ready");
      // Drop any sticky charge conic so re-open doesn't flash old swirl.
      cd.style.background = "";
      cd.style.opacity = "";
      return;
    }
    btn.style.display = "flex";
    const hk = String(i + 1); // abilities 1–4
    const mv = mvs[i];
    if (!mv) {
      btn.classList.add("empty");
      btn.classList.remove("charging", "charge-ready");
      cd.style.background = "";
      cd.style.opacity = "";
      renderSlotButton(btn, cd, "empty", () => "", true, undefined, hk);
      return;
    }
    btn.classList.remove("empty");
    const key = `player.move.${mv.id}`;
    const locked = !!mv.requiresBox && !hasBoxUnlocked(mv.requiresBox);
    const chargingHere = !!(charge && charge.slot === i);
    const readyHere = chargingHere && game.bowChargeReady;
    btn.classList.toggle("charging", chargingHere);
    btn.classList.toggle("charge-ready", readyHere);
    renderSlotButton(
      btn, cd,
      `${mv.id}|${locked}|${chargingHere ? game.bowChargeFrac.toFixed(2) : ""}`,
      () => `<span class="icon">${locked ? "🔒" : mv.icon}</span>${mv.name}`,
      locked || (!chargingHere && (onCooldown(key) || game.stamina < mv.staminaCost)),
      chargingHere ? undefined : key, // while charging, don't paint CD sweep
      hk,
    );
    // Charge dial: soft gold conic fill 0→1 (distinct from cooldown sweep).
    if (chargingHere) {
      const pct = Math.round(game.bowChargeFrac * 100);
      cd.style.background =
        `conic-gradient(rgba(232,197,71,${CHARGE_SWIRL_A}) ${pct}%, transparent ${pct}%)`;
      cd.style.opacity = "1"; // alpha is in the gradient color; keep overlay visible
    } else {
      // End of charge: restore default .cooldown CSS (was overridden inline).
      cd.style.background = "";
      cd.style.opacity = "";
    }
  });
  // Focus-card charge dial (one-box HUD under #focus) — hidden when not charging.
  paintFocusChargeDial();
  if (talkBtn) {
    talkBtn.style.display = talk.show ? "flex" : "none";
    if (talk.label !== talkLabelShown) {
      talkLabelShown = talk.label;
      talkBtn.innerHTML = `<span class="icon">💬</span>${talk.label}`;
    }
    // Always re-assert the hotkey after any label rebuild (innerHTML wipes it).
    if (talk.show) setSlotHotkey(talkBtn, "T");
    talkBtn.disabled = !talk.enabled;
  }
  if (interactBtn) {
    interactBtn.style.display = slot.show ? "flex" : "none";
    const shown = slot.icon + slot.label;
    if (shown !== interactShown) {
      interactShown = shown;
      interactBtn.innerHTML = `<span class="icon">${slot.icon}</span>${slot.label}`;
      // (Re)attach the cooldown sweep — innerHTML wiped it (same as the attack slot).
      if (!interactCd) { interactCd = document.createElement("span"); interactCd.className = "cooldown"; }
      interactBtn.appendChild(interactCd);
    }
    // F is Interact only outside combat (or with a prop focused). While fighting,
    // F is the primary attack — door soft-targets stay clickable without a keycap.
    if (slot.show) {
      const interactOwnsF = !!getPropFocus() || !engaged;
      setSlotHotkey(interactBtn, interactOwnsF ? "F" : null);
    }
    interactBtn.disabled = !slot.enabled;
    if (interactCd) renderCooldownSweep(interactCd, INTERACT_CD);
  }
  // Fishing slots (plan 10) — fishing.ts owns the state + click; we render.
  // The Cast/Reel slot's sweep is the reel-window reaction timer (fish.reel).
  // Mid-cast Space is the pull hotkey (fishing.ts); free cast is still click-water.
  if (fishBtn && fishCd) {
    fishBtn.style.display = fish.show ? "flex" : "none";
    if (fish.show) {
      const fishHk = fish.label === "Pull" || fish.label === "Hook!" ? "Spc" : undefined;
      renderSlotButton(
        fishBtn, fishCd,
        `${fish.icon}${fish.label}|${fish.enabled}`,
        () => `<span class="icon">${fish.icon}</span>${fish.label}`,
        !fish.enabled,
        REEL_CD,
        fishHk,
      );
    }
  }
  if (netBtn && netCd) {
    netBtn.style.display = net.show ? "flex" : "none";
    if (net.show) {
      renderSlotButton(
        netBtn, netCd,
        `${net.icon}${net.label}|${net.enabled}`,
        () => `<span class="icon">${net.icon}</span>${net.label}`,
        !net.enabled,
        // no cdKey: nets have no client cooldown (the soak lives in the label)
      );
    }
  }
  // Trapping slot (plan 11) — hunting.ts owns the state + click; we render.
  if (trapBtn && trapCd) {
    trapBtn.style.display = trap.show ? "flex" : "none";
    if (trap.show) {
      renderSlotButton(
        trapBtn, trapCd,
        `${trap.icon}${trap.label}|${trap.enabled}`,
        () => `<span class="icon">${trap.icon}</span>${trap.label}`,
        !trap.enabled,
        // no cdKey: the soak lives in the label, like nets
      );
    }
  }
  // Cooking slot (plan 25) — cooking.ts owns the state + click; we render. No
  // sweep key anymore: the pan-minigame overlay is its own timer.
  if (cookBtn && cookCd) {
    cookBtn.style.display = cook.show ? "flex" : "none";
    if (cook.show) {
      renderSlotButton(
        cookBtn, cookCd,
        `${cook.icon}${cook.label}|${cook.enabled}`,
        () => `<span class="icon">${cook.icon}</span>${cook.label}`,
        !cook.enabled,
      );
    }
  }
  // Extinguish own focused campfire (campfire.ts owns click).
  // MUST use renderSlotButton (keyed) — rewriting innerHTML every frame
  // destroyed the button under the cursor and dropped most clicks.
  if (extinguishBtn && extinguishCd) {
    extinguishBtn.style.display = extinguish.show ? "flex" : "none";
    if (extinguish.show) {
      renderSlotButton(
        extinguishBtn, extinguishCd,
        `${extinguish.icon}${extinguish.label}|${extinguish.enabled}`,
        () => `<span class="icon">${extinguish.icon}</span>${extinguish.label}`,
        !extinguish.enabled,
      );
    }
  }
  // Brewing slot (plan 18 CP3) — brewing.ts owns the state + click; we render.
  // Like the trap/net slots, the soak lives in the label (no sweep key).
  if (brewBtn && brewCd) {
    brewBtn.style.display = brew.show ? "flex" : "none";
    if (brew.show) {
      renderSlotButton(
        brewBtn, brewCd,
        `${brew.icon}${brew.label}|${brew.enabled}`,
        () => `<span class="icon">${brew.icon}</span>${brew.label}`,
        !brew.enabled,
        // no cdKey: the soak lives in the label, like traps/nets
      );
    }
  }
  // 5. Overhead HP bars over every in-combat remote player (same projection as the
  //    NPC bars). Single full-set call so leavers' bars get pruned.
  renderPlayerBars(camera, combatants());
}

/** Defeat presentation timeline (client-only; server owns the real respawn wait):
 *  0 → DEFEAT_DUR: topple onto the side
 *  DEFEAT_DUR → CORPSE_LINGER: body lies on the ground
 *  CORPSE_LINGER → +CORPSE_FADE: sink + scale out (no shared toonMat opacity)
 *  after: group.visible = false until npcRespawn
 *  Interest-enter on an already-dead NPC snaps past the fade (npcNetShow). */
function advanceDefeat(L: Live, dt: number): void {
  L.defeatT += dt;
  const t = L.defeatT;
  const flatK = 1; // settled topple
  if (t < CORPSE_LINGER) {
    const k = toppleK(t); // caps at 1 once past DEFEAT_DUR
    L.group.rotation.z = k * (Math.PI / 2);
    L.group.position.y = L.feetY + k * 0.3;
    L.group.scale.setScalar(1);
    L.group.visible = true;
    return;
  }
  if (t < CORPSE_LINGER + CORPSE_FADE) {
    const u = (t - CORPSE_LINGER) / CORPSE_FADE; // 0 → 1
    const ease = u * u; // ease-in sink
    L.group.rotation.z = flatK * (Math.PI / 2);
    L.group.position.y = L.feetY + 0.3 - ease * 0.9;
    L.group.scale.setScalar(1 - ease * 0.85);
    L.group.visible = true;
    return;
  }
  // Gone until the server respawn edge restores via npcRespawn.
  L.group.visible = false;
  L.group.scale.setScalar(1);
  L.group.rotation.z = flatK * (Math.PI / 2);
}
